import React, { useEffect } from "react";
import { Button, Space, Tooltip } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  LinkOutlined,
  UndoOutlined,
  RedoOutlined,
} from "@ant-design/icons";

import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";

type Props = {
  value?: string; // HTML
  onChange?: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minHeight?: number;
};

export const RichTextEditor: React.FC<Props> = ({
  value,
  onChange,
  placeholder = "Введите текст…",
  disabled,
  minHeight = 160,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // по желанию можно убрать код-блоки и т.п.
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
      }),
    ],
    content: value || "<p></p>",
    editable: !disabled,
    editorProps: {
      attributes: {
        "data-placeholder": placeholder,
        style: `min-height:${minHeight}px; outline:none;`,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html);
    },
  });

  // синхронизация значения из формы/сервера → в редактор
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "<p></p>";
    if (current !== next) editor.commands.setContent(next);
  }, [value, editor]);

  if (!editor) return null;

  const askLink = () => {
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Вставьте ссылку:", prev || "https://");
    if (url === null) return;
    if (url.trim() === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().setLink({ href: url.trim() }).run();
  };

  return (
    <div
      style={{
        border: "1px solid #d9d9d9",
        borderRadius: 8,
        overflow: "hidden",
        background: disabled ? "#fafafa" : "#fff",
      }}
    >
      <div style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>
        <Space size={6} wrap>
          <Tooltip title="Bold">
            <Button
              size="small"
              icon={<BoldOutlined />}
              type={editor.isActive("bold") ? "primary" : "default"}
              onClick={() => editor.chain().focus().toggleBold().run()}
              disabled={disabled}
            />
          </Tooltip>

          <Tooltip title="Italic">
            <Button
              size="small"
              icon={<ItalicOutlined />}
              type={editor.isActive("italic") ? "primary" : "default"}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              disabled={disabled}
            />
          </Tooltip>

          <Tooltip title="Bulleted list">
            <Button
              size="small"
              icon={<UnorderedListOutlined />}
              type={editor.isActive("bulletList") ? "primary" : "default"}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              disabled={disabled}
            />
          </Tooltip>

          <Tooltip title="Numbered list">
            <Button
              size="small"
              icon={<OrderedListOutlined />}
              type={editor.isActive("orderedList") ? "primary" : "default"}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              disabled={disabled}
            />
          </Tooltip>

          <Tooltip title="Link">
            <Button
              size="small"
              icon={<LinkOutlined />}
              onClick={askLink}
              disabled={disabled}
            />
          </Tooltip>

          <Tooltip title="Undo">
            <Button
              size="small"
              icon={<UndoOutlined />}
              onClick={() => editor.chain().focus().undo().run()}
              disabled={disabled || !editor.can().undo()}
            />
          </Tooltip>

          <Tooltip title="Redo">
            <Button
              size="small"
              icon={<RedoOutlined />}
              onClick={() => editor.chain().focus().redo().run()}
              disabled={disabled || !editor.can().redo()}
            />
          </Tooltip>
        </Space>
      </div>

      <div style={{ padding: 12 }}>
        <EditorContent editor={editor} />
      </div>

      <style>{`
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #bfbfbf;
          pointer-events: none;
          height: 0;
        }
        .ProseMirror p { margin: 0 0 0.6em 0; }
        .ProseMirror ul, .ProseMirror ol { padding-left: 1.4em; }
      `}</style>
    </div>
  );
};
